// Practice Details — the Billing Settings tab as one form (David, 2026-10-25: "looks more
// like the other bits"). The same label / field rows as the Notifications tab, a line of
// help where a field needs one, and one Save at the bottom. It replaces four cards on
// this tab and talks to their endpoints through their own helpers:
//
//   Card charging        PUT /features {card_charging}      super_admin
//   Card on file         collections rules                 admins edit, staff read;
//                                                             shown with statements
//   ERA auto-post, hold codes        /era-auto-post          super_admin
//   PM moves to patient              /pm-patient-transfer    admins
//   eCW status codes                 EcwStatusCodesCard      admins, eCW practices
//
// Save sends only what changed, part by part; a part that fails is named and the rest
// still save. Turning ERA auto-post on and emptying the hold list still ask first, as
// the cards did.

import React, { useEffect, useRef, useState } from "react";
import useCapabilities, { primeCapabilities } from "../../hooks/useCapabilities";
import { fetchRules, fetchSettings, saveRules } from "../collections/api";
import { changedKeys, toForm } from "../collections/CollectionsSettingsCard";
import {
  fetchEraAutoPost,
  formatMoney,
  parseHoldCarcs,
  saveEraAutoPost,
  saveHoldCarcs,
} from "../eraAutoPost/EraAutoPostCard";
import { fetchPmPatientTransfer, savePmPatientTransfer } from "../pmPatientTransfer/PmPatientTransferCard";
import EcwStatusCodesCard from "../data/EcwStatusCodesCard";
import "./BillingSettings.css";

const API_URL = process.env.REACT_APP_API_URL || "";

// Of the collections rules this form shows only the no-show switch (David, 2026-10-25):
// the statement-based rules (overdue charging, overdue after, statement email from-name
// and reply-to) are not on Settings. Their stored values are untouched, and changedKeys
// never sends a field that did not change.

function Row({ label, htmlFor, hint, children }) {
  return (
    <div className="bs-row">
      <label className="bs-label" htmlFor={htmlFor}>{label}</label>
      <div className="bs-field">
        <div className="bs-control">{children}</div>
        {hint && <div className="bs-hint">{hint}</div>}
      </div>
    </div>
  );
}

// Loads one setting for the practice; `null` until it answers, `error` if it did not.
function useLoad(practiceId, enabled, load) {
  const [value, setValue] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!practiceId || !enabled) return undefined;
    let cancelled = false;
    setValue(null);
    setError(null);
    load(practiceId)
      .then((v) => !cancelled && setValue(v))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
    // `load` is a module-level helper, stable across renders.
  }, [practiceId, enabled]);
  return [value, setValue, error];
}

export default function BillingSettingsForm({ practiceId, isAdmin, isSuperAdmin, isEcw }) {
  const gate = useCapabilities(practiceId);
  const rulesShown = !!(gate.features.collections || gate.features.statements);

  const [rules, setRules, rulesError] = useLoad(practiceId, rulesShown, fetchRules);
  const [account] = useLoad(practiceId, rulesShown, fetchSettings);
  const [era, setEra, eraError] = useLoad(practiceId, isSuperAdmin, fetchEraAutoPost);
  const [pm, setPm, pmError] = useLoad(practiceId, isAdmin, fetchPmPatientTransfer);
  const ecwRef = useRef(null);

  // Drafts: what the form shows, saved by the one Save.
  const [cardDraft, setCardDraft] = useState(null);
  const [form, setForm] = useState(null);
  const [eraDraft, setEraDraft] = useState(null);
  const [holdText, setHoldText] = useState("");
  const [pmDraft, setPmDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const cardOn = !!gate.features.card_charging;
  useEffect(() => setCardDraft(cardOn), [cardOn, practiceId]);
  useEffect(() => setForm(rules ? toForm(rules) : null), [rules]);
  useEffect(() => {
    setEraDraft(era ? !!era.enabled : null);
    setHoldText(era ? (era.hold_carcs || []).join(", ") : "");
  }, [era]);
  useEffect(() => setPmDraft(pm ? !!pm.trusted : null), [pm]);

  const setRule = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const ruleDisabled = !isAdmin || saving;
  const eraLocked = !era || era.migration_pending || saving;
  const holdsLocked = eraLocked || era.hold_carcs_migration_pending;
  const pmLocked = !pm || pm.migration_pending || saving;

  const saveCardCharging = async () => {
    if (!isSuperAdmin || gate.loading || cardDraft === cardOn) return;
    const response = await fetch(`${API_URL}/api/practices/${practiceId}/features`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ card_charging: cardDraft }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || "Could not save that change.");
    primeCapabilities(practiceId, body);
  };

  const saveCardRules = async () => {
    if (!isAdmin || !rules || !form) return;
    const patch = changedKeys(rules, form);
    if (!Object.keys(patch).length) return;
    setRules(await saveRules(practiceId, patch));
  };

  // Returns a note when the person declined a confirm, so the message can say so.
  const saveEra = async () => {
    if (!isSuperAdmin || !era || era.migration_pending) return null;
    let note = null;
    let next = era;
    if (eraDraft !== !!era.enabled) {
      const ok =
        !eraDraft ||
        window.confirm(
          "Turn on ERA auto-post? New ERAs that pass every check will be posted without anyone pressing Post. ERAs that fail a check are held in the queue with the reason.",
        );
      if (ok) {
        // Kept as soon as it lands, so a failing hold-list save below cannot leave the
        // form showing a switch that did save as unsaved.
        next = await saveEraAutoPost(practiceId, eraDraft);
        setEra(next);
      } else {
        note = "ERA auto-post was left off.";
        setEraDraft(false);
      }
    }
    if (!era.hold_carcs_migration_pending) {
      const codes = parseHoldCarcs(holdText);
      if (codes.join(", ") !== (era.hold_carcs || []).join(", ")) {
        const ok =
          codes.length > 0 ||
          window.confirm("Save an empty hold list? No adjustment code will hold an ERA for review for this practice.");
        if (ok) next = await saveHoldCarcs(practiceId, codes);
        else {
          note = [note, "The hold list was not changed."].filter(Boolean).join(" ");
          setHoldText((era.hold_carcs || []).join(", "));
        }
      }
    }
    setEra(next);
    return note;
  };

  const savePm = async () => {
    if (!isAdmin || !pm || pm.migration_pending || pmDraft === !!pm.trusted) return;
    setPm(await savePmPatientTransfer(practiceId, pmDraft));
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setMessage(null);
    const parts = [
      ["Card charging", saveCardCharging],
      ["Card on file", saveCardRules],
      ["ERA auto-post", saveEra],
      ["PM moves to patient", savePm],
      ["eCW status codes", () => ecwRef.current && ecwRef.current.save()],
    ];
    const failed = [];
    const notes = [];
    for (const [name, run] of parts) {
      try {
        const note = await run();
        if (typeof note === "string") notes.push(note);
      } catch (err) {
        failed.push(`${name}: ${err.message}`);
      }
    }
    // A declined confirm's note ("ERA auto-post was left off.") is kept either way.
    setMessage(
      failed.length
        ? { type: "error", text: [`Not saved — ${failed.join("; ")}.`, ...notes].join(" ") }
        : { type: "success", text: ["Settings saved.", ...notes].join(" ") },
    );
    setSaving(false);
  };

  const showCardSection = isSuperAdmin || rulesShown;
  const showPosting = isSuperAdmin || isAdmin;

  return (
    <div className="billing-settings" data-testid="billing-settings-form">
      {showCardSection && (
        <div className="integrations-section">
          <h3 className="practice-rules-section-title">Card payments</h3>
          {isSuperAdmin && (
            <Row label="Card charging" htmlFor="bs-card-charging" hint="Turn on only once the practice's merchant account is connected.">
              <input
                id="bs-card-charging"
                type="checkbox"
                checked={!!cardDraft}
                disabled={gate.loading || saving}
                onChange={(e) => setCardDraft(e.target.checked)}
              />
            </Row>
          )}
          {rulesShown && account && (
            <Row label="Payments account">
              <span className="bs-text">
                {account.stripe.enabled ? "Set up" : "Not set up"} · paid into{" "}
                {account.stripe.connectedAccount ? "the practice's own Stripe account" : "Atlas's Stripe account"}
              </span>
            </Row>
          )}
          {rulesShown && rulesError && <p className="bs-error">{rulesError}</p>}
          {rulesShown && form && (
            <>
              <Row label="No-show fees" htmlFor="bs-no-show" hint="May go on a card on file. Off: a no-show is never charged to a card.">
                <input
                  id="bs-no-show"
                  type="checkbox"
                  checked={form.chargeNoShowFee}
                  disabled={ruleDisabled}
                  onChange={(e) => setRule("chargeNoShowFee", e.target.checked)}
                />
              </Row>
              {account && account.consentText && (
                <Row label="Consent wording">
                  <details className="bs-details">
                    <summary>Version {account.consentTextVersion}: what a patient agrees to</summary>
                    <blockquote>{account.consentText}</blockquote>
                  </details>
                </Row>
              )}
            </>
          )}
        </div>
      )}

      {showPosting && (
        <div className="integrations-section">
          <h3 className="practice-rules-section-title">Posting</h3>
          {isSuperAdmin && eraError && <p className="bs-error">{eraError}</p>}
          {isSuperAdmin && era && (
            <>
              <Row
                label="ERA auto-post"
                htmlFor="bs-era"
                hint={
                  era.migration_pending
                    ? `Unavailable until ${era.migration} has run.`
                    : `Posts a clean ERA without anyone pressing Post; anything that fails a check is held. Last ${era.days} days: ${era.auto_posted_count} posted (${formatMoney(era.auto_posted_total)})${typeof era.held_count === "number" ? `, ${era.held_count} held` : ""}.`
                }
              >
                <input
                  id="bs-era"
                  type="checkbox"
                  checked={!!eraDraft}
                  disabled={eraLocked}
                  onChange={(e) => setEraDraft(e.target.checked)}
                />
              </Row>
              <Row
                label="Hold on codes"
                htmlFor="bs-hold-codes"
                hint={
                  era.hold_carcs_migration_pending
                    ? `The default list; editable once ${era.checks_migration} has run.`
                    : `An ERA with any of these codes waits for a person. Default: ${(era.hold_carcs_default || []).join(", ")}.`
                }
              >
                <input
                  id="bs-hold-codes"
                  className="bs-input"
                  type="text"
                  value={holdText}
                  disabled={holdsLocked}
                  onChange={(e) => setHoldText(e.target.value)}
                />
              </Row>
            </>
          )}
          {isAdmin && pmError && <p className="bs-error">{pmError}</p>}
          {isAdmin && pm && (
            <Row
              label="PM moves to patient"
              htmlFor="bs-pm"
              hint={
                pm.migration_pending
                  ? `Reads as on (the default) until ${pm.migration} has run.`
                  : "When the PM moves a charge to the patient and there is no ERA, count it as patient balance (“per PM, no ERA”). Off: it stays with insurance until an ERA says so."
              }
            >
              <input
                id="bs-pm"
                type="checkbox"
                checked={!!pmDraft}
                disabled={pmLocked}
                onChange={(e) => setPmDraft(e.target.checked)}
              />
            </Row>
          )}
        </div>
      )}

      {isAdmin && isEcw && <EcwStatusCodesCard ref={ecwRef} practiceId={practiceId} editable embedded />}

      {isAdmin && (
        <div className="bs-save">
          {message && (
            <div className={`form-message ${message.type}`}>
              {message.text}
              <button type="button" className="message-close-sm" onClick={() => setMessage(null)} aria-label="Dismiss">
                &times;
              </button>
            </div>
          )}
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      )}
    </div>
  );
}
